import { Injectable } from "@angular/core";
import { environment, UserService, LanguageService } from "@mypxplat/xplat/core";
import * as Inspector from "avo-inspector";
import { BehaviorSubject } from "rxjs";
import { ScriptService } from "./script.service";

/** Standard page properties used across AVO events. Optional UTM/sourceCode are omitted when null/undefined if using buildEventPayload with omitNulls. */
export interface AvoPageProperties {
  path: string;
  url: string;
  referrer: string;
  date: string;
  locale: string;
  name: string;
  utmSource?: string;
  utmId?: number;
  utmTerm?: string;
  utmMedium?: string;
  utmChannel?: string;
  utmCampaign?: string;
  sourceCode?: string | null;
}

@Injectable()
export class AvoService {
  public avoInitted: boolean = false;
  public avoInitted$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  // True only once Avo.initAvo() has actually run and wired up its destinations.
  // `avoInitted` merely means the init attempt finished (it is also set true when the
  // Segment script fails to load or initAvo throws). In those failure paths initAvo never
  // runs, so the generated Avo module's internal `Segment` destination is still undefined and
  // calling an event (e.g. loggedIn) would throw "undefined is not an object (evaluating
  // 'Segment.setUserProperties')". trackEvent() gates the real module on this flag so that we
  // hand back safe stubs whenever the destinations are not ready.
  public avoReady: boolean = false;
  public avoModule: any = null;
  private _initializing: Promise<boolean> | null = null;
  private _eventQueue: Array<{ eventName: string; args: any }> = [];

  /** Path of the previous SPA route (for referrer). Set by recordNavigation() on each NavigationEnd. */
  private _internalReferrerPath: string = "";
  private _currentPath: string = "";

  /** Removes keys with null, undefined, or empty string so Segment never receives them. */
  private _omitNullsFromPayload(props: Record<string, any> | null | undefined): Record<string, any> {
    if (props == null || typeof props !== "object") return {};
    const out: Record<string, any> = {};
    for (const key of Object.keys(props)) {
      const v = props[key];
      if (v === null || v === undefined) continue;
      if (typeof v === "string" && v === "") continue;
      out[key] = v;
    }
    return out;
  }

  private _customDestination = {
    make: (_env: any): void => {
      // console.log('make.')
    },
    logEvent: (eventName: string, eventProperties: any): void => {
      const clean = this._omitNullsFromPayload(eventProperties);
      if (typeof window !== "undefined" && (window as any).analytics) {
        (window as any).analytics.track(eventName, clean);
      }
    },
    identify: (_userId: string): void => {
      // (<any>window).analytics.identify(userId);
    },
    logPage: (pageName: string, eventProperties: object): void => {
      const clean = this._omitNullsFromPayload(eventProperties as Record<string, any>);
      if (typeof window !== "undefined" && (window as any).analytics) {
        (window as any).analytics.page(null, pageName, clean);
      }
    },
    setUserProperties: (userId: string, properties: any): void => {
      if (typeof window !== "undefined" && (window as any).analytics) {
        (window as any).analytics.identify(userId, properties);
      }
    },
  };

  public inspector: Inspector.AvoInspector;
  constructor(
    public userService: UserService,
    private _scriptService: ScriptService,
    private languageService: LanguageService
  ) {}

  /**
   * Call this on each Router NavigationEnd (e.g. in web-fender app) so that getPageProperties()
   * can use the previous SPA path as referrer instead of document.referrer.
   * @param pathOrUrl New path/URL after navigation (path only, e.g. /home or /products/detail/123). Query string is stripped.
   */
  recordNavigation(pathOrUrl: string): void {
    const path = pathOrUrl ? pathOrUrl.split("?")[0] : "";
    if (this._currentPath !== "") {
      this._internalReferrerPath = this._currentPath;
    }
    this._currentPath = path;
  }

  /**
   * Returns standard page properties for AVO event payloads.
   * Use with buildEventPayload() and pass the result to trackEvent().xxx().
   * When referrerOverride is not passed, uses the last SPA path from recordNavigation() if set, else document.referrer.
   */
  getPageProperties(options?: { pageName?: string; locale?: string; referrerOverride?: string }): AvoPageProperties {
    const pageName = options?.pageName ?? "Page";
    if (typeof window === "undefined") {
      const user = this.userService.user;
      const locale = options?.locale ?? (user ? `${user.language_code || "en"}-${user.country || "US"}` : (this.languageService?.selectedLanguage?.code ?? "en"));
      return {
        path: "",
        url: "",
        referrer: options?.referrerOverride ?? "",
        date: "",
        locale,
        name: pageName,
      };
    }
    const urlParams = new URLSearchParams(window.location.search);
    const user = this.userService.user;
    const locale = options?.locale ?? (user ? `${user.language_code || "en"}-${user.country || "US"}` : (this.languageService?.selectedLanguage?.code ?? navigator.language ?? "en"));
    const utmSourceParam = urlParams.get("utm_source");
    const utmIdParam = urlParams.get("utm_id");
    const utmTermParam = urlParams.get("utm_term");
    const utmMediumParam = urlParams.get("utm_medium");
    const utmChannelParam = urlParams.get("utm_channel");
    const utmCampaignParam = urlParams.get("utm_campaign");
    const sourceCode = typeof localStorage !== "undefined" ? (localStorage.getItem("sourceCode") ?? undefined) : undefined;
    let referrer = options?.referrerOverride !== undefined ? options.referrerOverride : this._internalReferrerPath || document.referrer || "";
    // Send full URL for referrer when it's an internal path (tracking plan: referrer must be full path)
    if (referrer && referrer.startsWith("/") && !referrer.startsWith("//")) {
      referrer = window.location.origin + referrer;
    }
    return {
      path: window.location.pathname,
      url: window.location.href,
      referrer,
      date: new Date().toISOString().split("T")[0],
      locale,
      name: pageName,
      ...(utmSourceParam ? { utmSource: utmSourceParam } : {}),
      ...(utmIdParam ? { utmId: parseInt(utmIdParam, 10) } : {}),
      ...(utmTermParam ? { utmTerm: utmTermParam } : {}),
      ...(utmMediumParam ? { utmMedium: utmMediumParam } : {}),
      ...(utmChannelParam ? { utmChannel: utmChannelParam } : {}),
      ...(utmCampaignParam ? { utmCampaign: utmCampaignParam } : {}),
      ...(sourceCode !== undefined ? { sourceCode } : {}),
    };
  }

  /**
   * Merges base and extra into a single payload. When omitNulls is true (default), keys with value null or undefined are excluded.
   */
  buildEventPayload<T extends Record<string, any>>(base: Partial<T>, extra: Partial<T>, options?: { omitNulls?: boolean }): Partial<T> {
    const omitNulls = options?.omitNulls !== false;
    const merged = { ...base, ...extra };
    if (!omitNulls) return merged;
    const out: Partial<T> = {};
    for (const key of Object.keys(merged)) {
      const v = merged[key];
      if (v !== null && v !== undefined) (out as any)[key] = v;
    }
    return out;
  }

  async getAvoModule(appId: string) {
    if (appId === "fender") {
      return import("../../../../../../../apps/web-fender/src/Avo");
    } else if (appId === "presonus_connect") {
      return import("../../../../../../../apps/web-connect/src/Avo");
    } else {
      throw new Error(`Invalid appId: ${appId}`);
    }
  }

  /**
   * Get system properties for AVO initialization (device category and site ID)
   */
  private getAvoSystemProperties(): { deviceCategory: string; siteId: string } {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return { deviceCategory: "desktop", siteId: "mypresonus" };
    }

    // Detect device category
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const deviceCategory = isMobile ? "mobile" : "desktop";

    // Site ID based on appId
    const siteId = this.userService.appId === "fender" ? "my_fender" : this.userService.appId === "presonus_connect" ? "presonus_connect" : "mypresonus";

    return { deviceCategory, siteId };
  }

  async initAvoFunctions(apiKey, appName, version) {
    if (this.avoInitted && this.avoModule && this.avoModule.default) {
      if (!environment.production) {
        console.log("[AVO] Already initialized, skipping");
      }
      return Promise.resolve(true);
    }

    if (this._initializing) {
      if (!environment.production) {
        console.log("[AVO] Initialization already in progress, returning existing promise");
      }
      return this._initializing;
    }
    this._initializing = (async () => {
      try {
        if (!environment.production) {
          console.log(`[AVO] Starting initialization for app: ${this.userService.appId}`);
        }

        // Load AVO module
        this.avoModule = await this.getAvoModule(this.userService.appId);

        if (!this.avoModule || !this.avoModule.default) {
          throw new Error("Failed to load AVO module: module.default is undefined");
        }

        // Verify the module has real functions (not just empty object)
        if (typeof this.avoModule.default.initAvo !== "function") {
          throw new Error("Failed to load AVO module: module.default.initAvo is not a function");
        }

        if (!environment.production) {
          console.log("[AVO] Module loaded successfully");
        }

        return new Promise<boolean>((resolve, reject) => {
          if (environment.avo.on) {
            if (!this.inspector && !this.avoInitted) {
              this._scriptService
                .loadScript(environment.segment.scriptID)
                .then((result: any) => {
                  if (result && result.loaded === false) {
                    this.avoInitted = true;
                    this.avoInitted$.next(true);
                    this._initializing = null;
                    resolve(false);
                    return;
                  }

                  try {
                    const Avo = this.avoModule.default;

                    if (!Avo || !Avo.initAvo) {
                      throw new Error("AVO module does not have initAvo function");
                    }

                    this.inspector = new Inspector.AvoInspector({
                      apiKey,
                      env: environment.production ? Inspector.AvoInspectorEnv.Prod : Inspector.AvoInspectorEnv.Dev,
                      version,
                      appName,
                    });

                    const isFender = this.userService.appId === "fender";
                    const systemProperties = this.getAvoSystemProperties();

                    if (isFender) {
                      Avo.initAvo(
                        {
                          env: environment.production ? Avo.AvoEnv.Prod : Avo.AvoEnv.Dev,
                          inspector: this.inspector,
                        },
                        systemProperties,
                        {},
                        this._customDestination
                      );
                    } else {
                      Avo.initAvo(
                        {
                          env: environment.production ? Avo.AvoEnv.Prod : Avo.AvoEnv.Dev,
                          inspector: this.inspector,
                        },
                        {},
                        this._customDestination
                      );
                    }

                    if (!environment.avo.on) this.inspector.enableLogging(false);

                    // initAvo() completed: destinations are wired, real module is safe to use.
                    this.avoReady = true;
                    this.avoInitted = true;
                    this.avoInitted$.next(true);
                    this._initializing = null;

                    if (!environment.production) {
                      console.log("[AVO] Initialization completed successfully");
                    }

                    this._replayQueuedEvents();

                    resolve(true);
                  } catch (error) {
                    console.error("[AVO] Error during AVO initialization:", error);
                    this.avoInitted = true;
                    this.avoInitted$.next(true);
                    this._initializing = null;
                    this._replayQueuedEvents();
                    resolve(false);
                  }
                })
                .catch((error) => {
                  console.error("[AVO] Failed to load Segment script:", error);
                  this.avoInitted = true;
                  this.avoInitted$.next(true);
                  this._initializing = null;
                  this._replayQueuedEvents();
                  resolve(false);
                });
            } else {
              if (this.avoModule && this.avoModule.default) {
                this.avoInitted = true;
                this.avoInitted$.next(true);
                this._initializing = null;
                this._replayQueuedEvents();
                resolve(true);
              } else {
                console.warn("[AVO] Inspector exists but avoModule is not loaded");
                this.avoInitted = true;
                this.avoInitted$.next(true);
                this._initializing = null;
                this._replayQueuedEvents();
                resolve(false);
              }
            }
          } else {
            this.avoInitted = true;
            this.avoInitted$.next(true);
            this._initializing = null;
            this._replayQueuedEvents();
            resolve(false);
          }
        });
      } catch (error) {
        console.error("[AVO] Failed to load AVO module:", error);
        this.avoModule = null;
        this.avoInitted = true;
        this.avoInitted$.next(true);
        this._initializing = null;
        this._replayQueuedEvents();
        return false;
      }
    })();

    return this._initializing;
  }

  returnEmptyMethods() {
    let emptyMethods: any = {};

    const commonEvents = [
      "loggedOut",
      "loggedIn",
      "loggedInMyFender",
      "accountCreated",
      "errorSignIn",
      "errorSignUp",
      "orderCompleted",
      "productViewed",
      "navigationClicked",
      "fileDownloaded",
      "transactionStarted",
      "transactionCompleted",
      "transactionCanceled",
      "registrationStarted",
      "registrationCompleted",
      "workspaceViewed",
      "communityViewed",
      "loadedAPage",
    ];

    commonEvents.forEach((eventName) => {
      emptyMethods[eventName] = (args?: any) => {
        if (environment.avo.on && !this.avoInitted) {
          this._eventQueue.push({ eventName, args });
          if (!environment.production) {
            console.log(`[AVO] Queued event ${eventName} (AVO not ready yet)`, args);
          }
        } else if (environment.avo.logMocks) {
          console.log(`[AVO] Event ${eventName} called but AVO is not initialized, args:`, args);
        } else if (!environment.production && eventName === "workspaceViewed") {
          console.warn("[AVO] workspaceViewed stub called but avo.on is false - event not queued");
        }
      };
    });

    if (this.avoModule && this.avoModule.default) {
      for (let i in this.avoModule.default) {
        if (!emptyMethods[i] && typeof this.avoModule.default[i] === "function") {
          emptyMethods[i] = (args?: any) => {
            if (environment.avo.on && !this.avoInitted) {
              this._eventQueue.push({ eventName: i, args });
              if (!environment.production) {
                console.log(`[AVO] Queued event ${i} (AVO not ready yet)`, args);
              }
            } else if (environment.avo.logMocks) {
              console.log(`Avo is off, but if it were on we would be recording this event: ${i}`);
              console.log("with these args:", args);
            }
          };
        }
      }
    }

    return emptyMethods;
  }

  private _replayQueuedEvents() {
    if (this._eventQueue.length === 0) {
      return;
    }

    if (!this.avoModule || !this.avoModule.default) {
      if (!environment.production) {
        console.warn("[AVO] Cannot replay queued events: avoModule.default is not available");
      }
      this._eventQueue = [];
      return;
    }

    const avoEvents = this.avoModule.default;
    const queuedCount = this._eventQueue.length;

    if (!environment.production) {
      console.log(`[AVO] Replaying ${queuedCount} queued events`);
    }

    this._eventQueue.forEach(({ eventName, args }) => {
      try {
        if (typeof avoEvents[eventName] === "function") {
          avoEvents[eventName](args);
          if (!environment.production) {
            console.log(`[AVO] Replayed event: ${eventName}`, args);
          }
        } else {
          if (!environment.production) {
            console.warn(`[AVO] Cannot replay event ${eventName}: function not found`);
          }
        }
      } catch (error) {
        console.error(`[AVO] Error replaying event ${eventName}:`, error);
      }
    });

    this._eventQueue = [];
  }

  trackEvent() {
    if (environment.avo.on) {
      if (this.avoReady && this.avoModule !== null && this.avoModule.default && typeof this.avoModule.default.initAvo === "function") {
        if (!environment.production) {
          console.log("[AVO] trackEvent() returning real Avo module, has workspaceViewed:", typeof this.avoModule?.default?.workspaceViewed);
        }
        return this.avoModule.default;
      } else {
        if (!environment.production) {
          console.warn("[AVO] trackEvent() called but AVO is not ready yet. avoInitted:", this.avoInitted, "avoModule:", this.avoModule !== null ? "loaded" : "null", "Returning stub methods.");
        }
        const stubs = this.returnEmptyMethods();
        if (!environment.production) {
          console.log("[AVO] Stubs has workspaceViewed:", typeof stubs.workspaceViewed);
        }
        return stubs;
      }
    } else {
      if (!environment.production) {
        console.warn("[AVO] trackEvent() avo.on is false, returning stub methods");
      }
      return this.returnEmptyMethods();
    }
  }
}
